import React from "react"
import SampleCard from "./SampleCard"
import LoadMoreButton from "./LoadMoreButton"
import { observer, inject } from "mobx-react"
import { VariableSizeList as List } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'

// Define an inline, memoized row component
const VirtualizedRow = React.memo(({ row, style, renderSampleRow, showSampleSelect }) => {
  return (
    <div style={{ ...style, height: "auto" }}>
      {renderSampleRow(row, showSampleSelect)}
    </div>
  )
})

@inject("sampleStore")
@observer
export default class Samples extends React.Component {
  static defaultProps = {
    showSampleSelect: false,
  }

  constructor(props) {
    super(props)
    this.state = {
      isClient: false,
      showSampleSelect: props.sampleStore.showSampleSelect,
      showSequencer: props.sampleStore.showSequencer
    }
    this.listRef = React.createRef()
  }

  componentDidMount() {
    this.setState({ isClient: true })
    window.addEventListener('resize', this.handleResize)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  handleResize = () => {
    if (this.listRef.current) {
      this.listRef.current.resetAfterIndex(0)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Check if the samples list changed or the selection mode changed
    const { sampleStore } = this.props
    if (
      sampleStore.samples.length !== prevProps.sampleStore.samples.length ||
      sampleStore.showSampleSelect !== prevState.showSampleSelect ||
      sampleStore.showSequencer !== prevState.showSequencer
    ) {
      if (this.listRef.current) {
        this.listRef.current.resetAfterIndex(0)
      }
    }
  }

  renderSampleRow(samples, showSampleSelect) {
    const sampleSlugs = samples.map((sample) => sample.slug)
    return (
      <div className="row" key={sampleSlugs}>
        {samples.map((sample) => {
          return (
            <SampleCard
              sample={sample}
              key={sample.slug}
              showSampleSelect={showSampleSelect}
            />
          )
        })}
      </div>
    )
  }

  renderRow = ({ index, style }) => {
    const { sampleStore } = this.props
    const samples = sampleStore.samples
    const rows = this.calculateRows(samples)
    const row = rows[index]
    const showSampleSelect = sampleStore.showSampleSelect || sampleStore.showSequencer;

    return (
      <VirtualizedRow
        row={row}
        style={style}
        aria-label={`Sample row`}
        renderSampleRow={(row) => this.renderSampleRow(row, showSampleSelect)}
        showSampleSelect={showSampleSelect}
      />
    )
  }

  getRowHeight = index => {
    const screenWidth = window.innerWidth;
    if (screenWidth <= 400) {
      return 1330; // Sample row height for screens 400px or less
    } else if (screenWidth <= 500) {
      return 1330; // Sample row height for screens 500px or less
    } else if (screenWidth <= 600) {
      return 1245; // Sample row height for screens 600px or less
    } else {
      return 349;  // Sample row height for desktop screens
    }
  }

  render() {
    const { pagyMetadata, sampleStore } = this.props
    const samples = sampleStore.samples
    const rows = this.calculateRows(samples)
    const { isClient, showSampleSelect, showSequencer } = this.state

    // Update local state with store values to trigger componentDidUpdate
    if (showSampleSelect !== sampleStore.showSampleSelect ||
        showSequencer !== sampleStore.showSequencer) {
      this.setState({
        showSampleSelect: sampleStore.showSampleSelect,
        showSequencer: sampleStore.showSequencer
      });
    }

    return (
      <>
        <div className="container" id="samples" aria-label={`Samples`} role="region">
          {!isClient && (
            <div className="ssr-content">
              {rows.map((row, index) => (
                <div key={index}>
                  {this.renderSampleRow(row, showSampleSelect)}
                </div>
              ))}
            </div>
          )}

          {isClient && (
            <AutoSizer disableHeight>
              {({ width }) => (
                <List
                  ref={this.listRef}
                  height={rows.length * this.getRowHeight()}
                  itemCount={rows.length}
                  itemSize={this.getRowHeight}
                  width={width}
                  overscanCount={3}
                  itemKey={index => rows[index].map(s => s.slug).join('-')}
                >
                  {this.renderRow}
                </List>
              )}
            </AutoSizer>
          )}
        </div>
        {pagyMetadata && <LoadMoreButton pagyMetadata={pagyMetadata} />}
      </>
    )
  }

  calculateRows(samples) {
    const rows = []
    let currentRow = []
    const samplesCount = samples.length

    samples.forEach((sample, idx) => {
      currentRow.push(sample)
      if (currentRow.length === 4 || samplesCount - 1 === idx) {
        rows.push(currentRow)
        currentRow = []
      }
    })
    return rows
  }
}
